import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { Coordinate } from "@/entities/tracking";
import { geolocation } from "@/shared/geo/geolocationStore";
import {
  COTONOU_FALLBACK,
  resetPlaceSearchSession,
  resolvePlaceSuggestion,
  reverseGeocode,
  searchAddresses,
  searchPlaces,
  shortAddress,
  type AddressSuggestion,
} from "@/shared/geo";
import * as Haptics from "@/shared/platform/haptics";
import { colors, semantic } from "@/shared/design-system";
import { AppText, Icon, Screen, Spinner } from "@/shared/ui";
import type { PickedLocation } from "../types";
import { PickerMap } from "./PickerMap";

type RecenterCommand = { coord: Coordinate; zoom?: number; nonce: number };

/**
 * Suggestions d'adresses : Google Places et Nominatim, fusionnés.
 *
 * Places connaît les COMMERCES (« Pharmacie Camp Guézo »), là où Nominatim ne
 * renvoyait que des rues anonymes. Nominatim complète les rues et adresses OSM
 * absentes de Google : la fusion dédupliquée donne davantage de choix.
 */
async function findAddresses(
  query: string,
  near: Coordinate,
  signal: AbortSignal,
): Promise<AddressSuggestion[]> {
  const [fromGoogle, fromNominatim] = await Promise.all([
    searchPlaces(query, near),
    searchAddresses(query, near, signal, 8),
  ]);
  if (signal.aborted) return [];

  const seen = new Set<string>();
  return [...fromGoogle, ...fromNominatim]
    .filter((suggestion) => {
      // Les fournisseurs n'ont pas le même identifiant : le libellé complet
      // normalisé est le meilleur anti-doublon inter-source disponible.
      const key = suggestion.fullName.trim().toLocaleLowerCase("fr");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 16);
}

/**
 * Position de l'appareil.
 *
 * `maxAgeMs` distingue les deux usages, et ce n'est pas un détail :
 *
 * - **ouverture du sélecteur** (valeur large) : on centre la carte. Le cache
 *   suffit amplement. Exiger un point de moins de 15 s rallumait le GPS à
 *   chaque ouverture — et sur Firefox, où la permission n'est retenue que si
 *   « Retenir ce choix » est coché, chaque appel réaffichait l'invite. Modifier
 *   son adresse redemandait donc l'autorisation à chaque fois.
 * - **bouton « ma position »** (valeur courte) : l'utilisateur demande
 *   explicitement un point à jour, le coût est justifié.
 */
async function getDevicePosition(maxAgeMs?: number): Promise<Coordinate | null> {
  return geolocation.ensurePosition(maxAgeMs);
}

/**
 * Sélecteur d'emplacement : la carte glisse sous un pin fixe, l'adresse se
 * résout au repos.
 *
 * ## Ce que le web change
 *
 * **Une seule carte au lieu de deux.** Le natif embarquait `GooglePickerMap`
 * (react-native-maps, dev build + clé Maps requis) ET `LeafletPickerMap`
 * (Leaflet dans une WebView) en repli. `PickerMap` remplace les deux.
 *
 * **Ce n'est PAS un écran de route, mais une surcouche.** Il était atteint par
 * `navigate("/location-picker")`, ce qui DÉMONTAIT l'écran appelant : au retour,
 * le composeur de course repartait de zéro et réécrasait l'adresse choisie avec
 * la position GPS en cache — le point sélectionné n'était jamais pris en compte.
 * Rendu en surcouche au-dessus de l'appelant, celui-ci reste monté : son état
 * (articles saisis, arrêts, contacts) survit, et la promesse de `pick()` se
 * résout dans un composant vivant.
 *
 * **La géolocalisation devient une permission de navigateur**, liée à
 * l'origine et révocable dans les réglages du site. Un refus est définitif
 * pour la session : le bouton « ma position » ne peut PAS re-solliciter
 * l'utilisateur. D'où le message d'aide plutôt qu'un simple retour vibré.
 */
type Props = {
  /** Centre initial (ex. adresse déjà choisie). `null` → position de l'appareil. */
  initial?: Coordinate | null;
  title?: string;
  /** Fermeture : point confirmé, ou `null` si l'utilisateur renonce. */
  onDone: (result: PickedLocation | null) => void;
};

export function LocationPickerOverlay({ initial, title, onDone }: Props) {
  const searchId = useId();

  const hasExplicitInitial =
    initial != null && Number.isFinite(initial.latitude) && Number.isFinite(initial.longitude);

  const initialCenterRef = useRef<Coordinate>(
    hasExplicitInitial ? { latitude: initial.latitude, longitude: initial.longitude } : COTONOU_FALLBACK,
  );

  const centerRef = useRef<Coordinate>(initialCenterRef.current);
  const fullAddressRef = useRef<string | undefined>(undefined);
  const [displayAddress, setDisplayAddress] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);

  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [recenter, setRecenter] = useState<RecenterCommand | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  /**
   * Une adresse choisie dans la liste n'a pas pu être résolue en coordonnées.
   *
   * Ce cas était TOTALEMENT silencieux, et c'est ce qui le rendait dangereux :
   * cf. `handleSelectSuggestion`.
   */
  const [resolveFailed, setResolveFailed] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      abortRef.current?.abort();
      searchAbortRef.current?.abort();
      resetPlaceSearchSession();
    };
  }, []);

  // Échap referme sans choisir — réflexe attendu d'une surcouche sur web, là où
  // le mobile compte sur le chevron de retour.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDone(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDone]);

  useEffect(() => {
    if (hasExplicitInitial) return;
    let cancelled = false;
    void (async () => {
      const coord = await getDevicePosition(5 * 60_000);
      if (coord && !cancelled) {
        setRecenter({ coord, zoom: 16, nonce: Date.now() });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    const q = search.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setIsSearching(false);
      searchAbortRef.current?.abort();
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      resetPlaceSearchSession();
      return;
    }

    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchAbortRef.current?.abort();
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      void findAddresses(q, centerRef.current, ctrl.signal)
        .then((hits) => {
          if (ctrl.signal.aborted) return;
          setSuggestions(hits);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setIsSearching(false);
        });
    }, 550);
  }, [search]);

  const handleSelectSuggestion = async (s: AddressSuggestion) => {
    Haptics.selectionAsync();
    skipNextSearchRef.current = true;
    setSearch(s.label);
    setSuggestions([]);

    // Une suggestion Google n'embarque pas ses coordonnées : elles arrivent au
    // Place Details. On affiche donc le libellé connu tout de suite, et la carte
    // se déplace une fois le point résolu — plutôt qu'une liste qui se ferme
    // sur un écran figé.
    //
    // ⚠️ MAIS ON GARDE DE QUOI REVENIR EN ARRIÈRE.
    //
    // Le libellé était auparavant posé sur `fullAddressRef` — la valeur RENDUE
    // à la confirmation — avant même de savoir si le point existait. Quand la
    // résolution échouait (quota Places, clé restreinte, réseau), on sortait en
    // silence : la carte n'avait pas bougé, mais l'écran affichait désormais
    // l'adresse cherchée. L'utilisateur confirmait donc une adresse JUSTE
    // posée sur des coordonnées FAUSSES — celles du centre précédent.
    //
    // Sur le formulaire de livraison, ce centre est le point de retrait : on
    // obtenait une « livraison » à l'adresse affichée du destinataire, mais aux
    // coordonnées de l'expéditeur. D'où l'avertissement « Retrait et livraison
    // au même endroit » sur deux adresses visiblement différentes.
    const previousFullAddress = fullAddressRef.current;
    const previousDisplay = displayAddress;

    setResolveFailed(false);
    fullAddressRef.current = s.fullName;
    setDisplayAddress(shortAddress(s.fullName));
    setIsResolving(true);

    const resolved = await resolvePlaceSuggestion(s);
    if (!resolved) {
      // Détails indisponibles : on ne recentre pas sur un point inventé — et
      // SURTOUT on restitue l'adresse d'avant, pour qu'elle continue de décrire
      // le point où le pin se trouve réellement.
      fullAddressRef.current = previousFullAddress;
      setDisplayAddress(previousDisplay);
      setResolveFailed(true);
      setIsResolving(false);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      return;
    }

    fullAddressRef.current = resolved.fullName;
    setDisplayAddress(shortAddress(resolved.fullName));
    // `handleCenterChange` prendra le relais à l'arrêt de la carte : il relance
    // un reverse-geocoding qui remettra `isResolving` à false.
    setRecenter({ coord: resolved.coord, zoom: 16, nonce: Date.now() });
  };

  const handleCenterChange = useCallback((coord: Coordinate) => {
    centerRef.current = coord;
    // Le pin a bougé : l'échec précédent ne décrit plus la situation.
    setResolveFailed(false);
    setIsResolving(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    debounceRef.current = setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      void reverseGeocode(coord, ctrl.signal)
        .then((name) => {
          if (ctrl.signal.aborted) return;
          fullAddressRef.current = name ?? undefined;
          setDisplayAddress(name ? shortAddress(name) : null);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setIsResolving(false);
        });
    }, 450);
  }, []);

  const handleSearch = async () => {
    const q = search.trim();
    if (q.length < 3) return;
    if (suggestions.length > 0) {
      await handleSelectSuggestion(suggestions[0]);
      return;
    }
    setIsSearching(true);
    try {
      // Entrée pressée sans qu'aucune suggestion ne soit encore arrivée : on
      // relance la même recherche que la liste, Places puis Nominatim.
      const hits = await findAddresses(q, centerRef.current, new AbortController().signal);
      if (hits.length > 0) {
        await handleSelectSuggestion(hits[0]);
      } else {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleMyLocation = async () => {
    // Appui EXPLICITE sur « ma position » : c'est l'action, elle exige un point.
    // `requirePosition` ouvre le modal avec la marche à suivre quand
    // l'autorisation manque — le natif se contentait d'une vibration, et sur
    // web un second appui ne relance rien du tout : le refus est mémorisé par
    // origine et seule une visite aux réglages du site peut le lever.
    //
    // ⚠️ L'OUVERTURE du sélecteur, elle, reste sur `ensurePosition` (plus haut) :
    // centrer une carte n'est pas une action de l'utilisateur, et faire surgir
    // le modal à chaque ouverture serait exactement le harcèlement qu'on évite.
    const coord = await geolocation.requirePosition(15_000);
    if (!coord) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      setLocationDenied(true);
      return;
    }
    setLocationDenied(false);
    setRecenter({ coord, zoom: 16, nonce: Date.now() });
  };

  const handleConfirm = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    onDone({
      latitude: centerRef.current.latitude,
      longitude: centerRef.current.longitude,
      address: fullAddressRef.current,
    });
  };

  return (
    <Screen backgroundColor={colors.white} edges={["top", "left", "right"]}>
      <div className="relative min-h-0 flex-1">
        {/* `isolate` est INDISPENSABLE, pas décoratif.
            Leaflet empile ses panes à `z-index: 400` et plus. Ce conteneur est
            positionné mais sans z-index : il ne crée donc aucun contexte
            d'empilement, et ces 400 remontent au niveau du parent, où ils
            passent devant tous les frères suivants — barre de recherche, pin
            central et bouton « ma position » disparaissaient sous les tuiles.
            `isolation: isolate` confine les z-index de la carte à ce bloc ;
            l'ordre du DOM redevient seul juge pour les overlays qui suivent. */}
        <div className="absolute inset-0 isolate">
          <PickerMap
            initialCenter={initialCenterRef.current}
            onCenterChange={handleCenterChange}
            recenter={recenter}
          />
        </div>

        {/* Pin fixe au centre — overlay DOM, hors carte. `pointer-events-none`
            remplace `pointerEvents="none"` : sans lui, le pin capterait le
            glissement et la carte ne bougerait pas. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="flex flex-col items-center" style={{ transform: "translateY(-23px)" }}>
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-full border-[3px] border-white bg-or-500">
              <Icon name="location" size={20} color={semantic.contentOnBrand} />
            </span>
            <span className="-mt-px h-3 w-[3px] bg-or-500" />
          </div>
          <span className="absolute h-1.5 w-1.5 rounded-full bg-navy-900/55" />
        </div>

        {/* Barre haute : retour + recherche */}
        <div className="absolute left-3 right-3 top-2 flex flex-row items-center gap-2.5">
          <button
            type="button"
            onClick={() => onDone(null)}
            aria-label="Retour"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-md"
          >
            <Icon name="chevron-back" size={22} color={colors.navy900} />
          </button>
          {/* `<form>` : la touche Entrée lance la recherche — équivalent de
              `onSubmitEditing` + `returnKeyType="search"`. */}
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSearch();
            }}
            className="flex h-11 min-w-0 flex-1 flex-row items-center gap-2 rounded-full bg-white px-3.5 shadow-md"
          >
            <Icon name="search" size={18} color={colors.gray500} />
            <label htmlFor={searchId} className="sr-only">
              Rechercher une adresse
            </label>
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une adresse"
              autoComplete="off"
              className="min-w-0 flex-1 bg-transparent text-sm text-navy-900 outline-none placeholder:text-gray-500"
            />
            {isSearching && <Spinner size={16} color={colors.or500} />}
          </form>
        </div>

        {/* Suggestions (autocomplétion) */}
        {suggestions.length > 0 && (
          <ul className="absolute left-[68px] right-3 top-[60px] max-h-64 overflow-y-auto rounded-2xl bg-white py-1 shadow-lg">
            {suggestions.map((s, i) => (
              <li key={s.id} className={i < suggestions.length - 1 ? "border-b border-gray-200" : ""}>
                <button
                  type="button"
                  onClick={() => void handleSelectSuggestion(s)}
                  className="flex w-full flex-row items-start gap-2.5 px-3.5 py-3 text-left"
                >
                  <span className="mt-px shrink-0">
                    <Icon name="location-outline" size={18} color={colors.gray500} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <AppText variant="label" className="line-clamp-1 block">
                      {s.label}
                    </AppText>
                    {s.fullName !== s.label && (
                      <AppText
                        variant="tiny"
                        color={colors.gray500}
                        className="mt-0.5 line-clamp-1 block"
                      >
                        {s.fullName}
                      </AppText>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={handleMyLocation}
          aria-label="Utiliser ma position"
          className="absolute bottom-[210px] right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg"
        >
          <Icon name="locate" size={20} color={colors.navy900} />
        </button>
      </div>

      {/* Carte info bas : adresse + confirmation */}
      <div className="flex flex-col gap-3.5 rounded-t-3xl bg-white px-5 pb-8 pt-4.5 shadow-[0_-2px_12px_rgba(0,0,0,0.1)]">
        <AppText as="h1" variant="eyebrow" color={colors.gray500}>
          {title ?? "Choisir l'emplacement"}
        </AppText>

        {locationDenied && (
          <AppText as="p" role="alert" variant="tiny" color={colors.sos600}>
            Position indisponible. Autorise la localisation dans les réglages du site, ou place le
            pin à la main.
          </AppText>
        )}

        {/* Adresse trouvée dans la liste, mais impossible à situer sur la carte.
            Le dire est ESSENTIEL : sans ce message, l'écran gardait le libellé
            cherché sur un pin qui n'avait pas bougé, et le point confirmé était
            celui d'avant. */}
        {resolveFailed && (
          <AppText as="p" role="alert" variant="tiny" color={colors.sos600}>
            Impossible de situer cette adresse sur la carte. Place le pin à la main, ou
            réessaie.
          </AppText>
        )}

        {/* `aria-live` : l'adresse se met à jour toute seule quand la carte
            s'arrête — sans annonce, le changement passe inaperçu. */}
        <div aria-live="polite" className="flex min-h-10 flex-row items-center gap-2.5">
          <Icon name="location-outline" size={18} color={colors.or700} />
          <div className="min-w-0 flex-1">
            {isResolving ? (
              <AppText variant="label" color={colors.gray500}>
                Localisation…
              </AppText>
            ) : (
              <AppText as="p" variant="labelBold" className="line-clamp-2">
                {displayAddress ?? "Point sélectionné (adresse introuvable)"}
              </AppText>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          className="flex h-[54px] w-full items-center justify-center rounded-2xl bg-or-500"
        >
          <AppText variant="button" color={semantic.contentOnBrand}>
            Confirmer ce point
          </AppText>
        </button>
      </div>
    </Screen>
  );
}
